import * as React from 'react';

import { enumLotStatus } from '@rematter/frontend-lib-api-client';
import { ComboBox, useComboBoxOptions } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';

import { inventoryLotsQueryAtom } from '../../../_graphql/queryInventoryLots';
import { useTranslations } from '../../../i18n/useTranslations';
import type { InventoryLotRecord } from '../../../types/inventoryLot';

type LotSelectorProps = {
  facilityId: string;
  selectedLotId: string | null;
  onSelect: (lotId: string | null) => void;
  excludeLotIds?: string[];
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
};

export const LotSelector = ({
  facilityId,
  selectedLotId,
  onSelect,
  excludeLotIds = [],
  label,
  placeholder,
  isRequired = false,
  isDisabled = false,
}: LotSelectorProps) => {
  const t = useTranslations();

  const $lotsQuery = React.useMemo(
    () =>
      loadable(
        inventoryLotsQueryAtom(() => ({
          variables: {
            facilityId,
            status: enumLotStatus.Active,
            limit: 100,
            offset: 0,
          },
        })),
      ),
    [facilityId],
  );

  const lotsResult = useAtomValue($lotsQuery);
  const isLoading = lotsResult.state === 'loading';
  const queryResult = lotsResult.state === 'hasData' ? lotsResult.data : null;
  const lotsData = queryResult?.data?.lots ?? [];

  const lots = React.useMemo(() => {
    if (!lotsData.length) return [];
    return lotsData.filter(
      (lot: InventoryLotRecord) => !excludeLotIds.includes(lot.id),
    );
  }, [lotsData, excludeLotIds]);

  const options = React.useMemo(
    () =>
      lots.map((lot: InventoryLotRecord) => ({
        id: lot.id,
        label: lot.lotNumber,
        subLabel: lot.description || undefined,
      })),
    [lots],
  );

  const { query, onChangeQuery, currentOption, filteredOptions } =
    useComboBoxOptions({
      options,
      value: selectedLotId || '',
      disabled: isDisabled,
      maxResults: 20,
    });

  const value = React.useMemo(
    () => (selectedLotId ? [selectedLotId] : []),
    [selectedLotId],
  );

  const selection = React.useMemo(
    () => [
      {
        label: currentOption?.label,
      },
    ],
    [currentOption],
  );

  const handleChange = React.useCallback(
    (values: string[]) => {
      onSelect(values.at(0) || null);
    },
    [onSelect],
  );

  return (
    <ComboBox<string>
      className="@LotSelector"
      label={label || t.lotsListView.allocation.selectLot}
      placeholder={placeholder || t.lotsListView.allocation.selectLot}
      query={query}
      onChangeQuery={onChangeQuery}
      options={filteredOptions}
      isLoading={isLoading}
      value={value}
      onChange={handleChange}
      getSelection={() => selection}
      isRequired={isRequired}
      isDisabled={isDisabled}
    />
  );
};
